import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { STATUS, fmtD } from "./complianceUtils";
import { StatusBadge } from "./ComplianceBadges";

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shift = (s, n) => { const [y, m] = s.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); return ym(d); };

export function ComplianceCalendar({ categories = [] }) {
  const [start, setStart] = useState(ym(new Date()));
  const [months, setMonths] = useState(12);
  const [category, setCategory] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    API.get("/compliance/calendar", { params: { start, months, category: category === "all" ? "" : category } })
      .then((r) => live && setData(r.data)).catch(() => live && setData(null)).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [start, months, category]);

  return (
    <div className="space-y-4" data-testid="compliance-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setStart((s) => shift(s, -1))} data-testid="calendar-prev"><CaretLeft size={14} /></Button>
          <input type="month" value={start} onChange={(e) => e.target.value && setStart(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm" data-testid="calendar-start" />
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setStart((s) => shift(s, 1))} data-testid="calendar-next"><CaretRight size={14} /></Button>
          <Button variant="ghost" size="sm" className="h-8" onClick={() => setStart(ym(new Date()))} data-testid="calendar-today">Today</Button>
        </div>
        <div className="flex gap-1">
          {[6, 12, 24].map((n) => <Button key={n} size="sm" variant={months === n ? "default" : "outline"} className="h-8" onClick={() => setMonths(n)} data-testid={`calendar-months-${n}`}>{n} mo</Button>)}
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-8 w-40" data-testid="calendar-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-muted-foreground">
          {Object.entries(STATUS).filter(([k]) => k !== "missing").map(([k, s]) => <span key={k} className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${s.dot}`} />{s.label}</span>)}
        </div>
      </div>

      {loading && !data ? <div className="h-48 animate-pulse rounded-lg bg-muted/40" /> : !data ? <p className="text-sm text-muted-foreground">Couldn't load the calendar.</p> : (
        <>
          {data.overdue_before_start.length > 0 && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="calendar-overdue">
              <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-2">Already expired before {data.start}</p>
              <div className="flex flex-wrap gap-2">
                {data.overdue_before_start.map((r) => <Item key={r.id} r={r} />)}
              </div>
            </div>
          )}
          <div className="overflow-x-auto pb-2">
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${data.months.length}, minmax(168px, 1fr))`, minWidth: data.months.length * 176 }}>
              {data.months.map((m) => {
                const now = ym(new Date()) === m.month;
                return (
                  <div key={m.month} className={`rounded-lg border ${now ? "border-primary/50 bg-primary/5" : "border-border bg-card"} p-2 min-h-[140px]`} data-testid={`calendar-month-${m.month}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold">{m.label}</p>
                      <span className="text-[10px] text-muted-foreground">{m.items.length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {m.items.length === 0 && <p className="text-[11px] text-muted-foreground/60 italic">Nothing due</p>}
                      {m.items.map((r) => <Item key={r.id} r={r} compact />)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {data.missing.length > 0 && (
            <div className="rounded-lg border border-border bg-muted/20 p-3" data-testid="calendar-missing">
              <p className="text-xs font-semibold mb-2">{data.missing.length} requirement{data.missing.length !== 1 ? "s" : ""} with no certificate on file</p>
              <div className="flex flex-wrap gap-2">
                {data.missing.map((r) => <Link key={r.id} to={`/compliance/properties/${r.property_id}`} className="rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:border-primary/50"><span className="font-medium">{r.type_short}</span> · {r.property_name}</Link>)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Item({ r, compact }) {
  const s = STATUS[r.status] || STATUS.missing;
  return (
    <Link to={`/compliance/properties/${r.property_id}`} className={`block rounded-md border bg-background px-2 py-1.5 text-[11px] hover:shadow-sm transition-shadow ${s.cls}`} data-testid={`calendar-item-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold truncate">{r.type_short}</span>
        <span className="shrink-0 tabular-nums">{fmtD(r.expiry_date).slice(0, 6)}</span>
      </div>
      <p className="truncate text-foreground/80">{r.property_name}</p>
      {!compact && <StatusBadge status={r.status} small />}
    </Link>
  );
}
